import * as React from 'react'
import { CheckBox, Label } from 'react-rails-form-helpers'
import * as API from '../lib/api'
import { PaymentDetails } from '../lib/payment_details'
import { TaxInformation } from '../lib/tax_information'
import { PaymentProfileForm } from '../PaymentForm/PaymentProfileForm'
import { PaymentTokenForm } from '../PaymentForm/PaymentTokenForm'
import { UnifiedCreditCardForm } from '../PaymentForm/UnifiedCreditCardForm'
import { PromoCodeForm } from '../PromoCodeForm'

// Reuses the existing payment pieces verbatim, so the submit payload stays
// identical to the control variant (`payment_profile[id]` for a saved card,
// `credit_card` fields tokenized into `accept_js_token` for a new card). The
// host owns submit + Accept.js tokenization; this body only gates the CTA on
// the two acknowledgments.

interface IProps {
  trial: boolean
  trialLength: number
  paymentDetails: PaymentDetails
  taxInformation: TaxInformation
  // Injection point for deterministic date math in tests; defaults to now.
  today?: Date

  paymentProfiles: API.IPaymentProfile[]
  selectedPaymentProfileId?: number
  creditCard?: API.ICreditCard
  paymentType: string
  // Set by the host once a new card tokenizes; swaps the live card input for the
  // token payload so the in-flight submit posts the token, not the PAN.
  paymentToken?: API.IPaymentToken

  allowPromoCode: boolean
  promoCode: string
  promoErrors: string[]
  applyPromoCode: (e: React.SyntheticEvent) => void

  ctaText: string
  onCashierSubmit: (event: React.MouseEvent<HTMLElement>) => void

  // Seeded from the shell's hoisted value so neither a server round-trip nor an
  // in-app step navigation makes a user re-tick it; carries the server's
  // `cashier.terms_of_service` plus any tick since.
  termsOfService: boolean

  // Client-side comprehension gate (never posted); seeded the same way so it
  // doesn't reset to false when the step body remounts.
  chargeAcknowledged?: boolean

  // Report ticks up so the shell can preserve them across the body unmounting.
  onChargeAcknowledgedChange?: (value: boolean) => void
  onTermsAcceptedChange?: (value: boolean) => void
}

interface IState {
  // Show the new-card input instead of the saved-card selector. Mirrors the
  // control: a returning member with saved cards starts on the selector.
  addCard: boolean
  // Both required to enable the CTA. Only `termsAccepted` is posted (as
  // `cashier[terms_of_service]`); the charge box is a client-side gate.
  chargeAcknowledged: boolean
  termsAccepted: boolean
}

// Matches the control checkout's currency rendering (`Cart#formatPrice`).
function formatCurrency(amount: number): string {
  return `$${amount.toFixed(2)}`
}

// Matches the control's date rendering (`TermsOfService#endOfTrial`).
function formatDate(date: Date): string {
  return date.toLocaleString('en-us', { month: 'long', day: 'numeric', year: 'numeric' })
}

function addDays(date: Date, days: number): Date {
  const result = new Date(date)
  result.setDate(result.getDate() + days)
  return result
}

export class Step3Payment extends React.Component<IProps, IState> {
  constructor(props: IProps) {
    super(props)

    const { paymentProfiles, paymentType, termsOfService, chargeAcknowledged } = props

    this.state = {
      addCard: paymentType === 'credit_card' || paymentProfiles.length === 0,
      chargeAcknowledged: chargeAcknowledged || false,
      termsAccepted: termsOfService,
    }
  }

  public handleAddNewCardClick = () => {
    this.setState({ addCard: true })
  }

  public handleCancelAddNewCardClick = () => {
    this.setState({ addCard: false })
  }

  public handleChargeAck = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { checked } = event.currentTarget
    this.setState({ chargeAcknowledged: checked })
    if (this.props.onChargeAcknowledgedChange) {
      this.props.onChargeAcknowledgedChange(checked)
    }
  }

  public handleTermsAccepted = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { checked } = event.currentTarget
    this.setState({ termsAccepted: checked })
    if (this.props.onTermsAcceptedChange) {
      this.props.onTermsAcceptedChange(checked)
    }
  }

  public render() {
    const { ctaText, onCashierSubmit } = this.props
    const { chargeAcknowledged, termsAccepted } = this.state
    const acknowledged = chargeAcknowledged && termsAccepted

    // The "What Happens Next" timeline is rendered full-bleed by the shell
    // above this body; this body owns only the card area, promo, acks, and CTA.
    return (
      <div className="step3-payment" data-testid="step3-payment">
        <section className="step3-payment__card">{this.renderCardArea()}</section>

        {this.renderPromoCode()}

        <fieldset className="step3-payment__acknowledgments">
          {this.renderChargeAcknowledgment()}
          {this.renderTermsAcknowledgment()}
        </fieldset>

        <button
          type="button"
          className="button trial-redesign__submit step3-payment__submit"
          data-testid="trial-redesign-submit"
          data-disable-with="Please Wait..."
          disabled={!acknowledged}
          onClick={onCashierSubmit}
        >
          {ctaText}
        </button>
      </div>
    )
  }

  // Mirrors `PaymentForm.renderForms`: a chosen saved card shows the selector,
  // "Add New Card" (or no saved cards) shows the unified input, and once the
  // host tokenizes the new card the token payload replaces the live input so
  // submit posts `accept_js_token` rather than the raw PAN.
  private renderCardArea() {
    const { paymentProfiles, selectedPaymentProfileId, creditCard, paymentToken } = this.props
    const { addCard } = this.state

    if (addCard) {
      if (paymentToken) {
        return <PaymentTokenForm {...paymentToken} />
      }

      return (
        <UnifiedCreditCardForm
          creditCard={creditCard}
          handleCancelAddNewCardClick={this.handleCancelAddNewCardClick}
          allowCancelling={paymentProfiles.length > 0}
        />
      )
    }

    return (
      <PaymentProfileForm
        selectedProfileId={selectedPaymentProfileId}
        paymentProfiles={paymentProfiles}
        handleAddNewCardClick={this.handleAddNewCardClick}
      />
    )
  }

  private renderPromoCode() {
    const { allowPromoCode, promoCode, promoErrors, applyPromoCode } = this.props

    if (!allowPromoCode) {
      return null
    }

    return (
      <div className="step3-payment__promo">
        <PromoCodeForm
          promoCode={promoCode}
          applyPromoCode={applyPromoCode}
          errors={promoErrors}
        />
      </div>
    )
  }

  // For a monthly plan `paymentDetails.total` is the per-installment amount, so
  // the trial copy names it "first monthly installment" — otherwise the box a
  // user legally affirms would understate a recurring charge as a one-time one.
  // The paid flow has no trial window, so it affirms the annual commitment.
  private renderChargeAcknowledgment() {
    const { trial, trialLength, paymentDetails, taxInformation, today } = this.props
    const amount = formatCurrency(paymentDetails.total({ taxInformation }))
    const monthly = paymentDetails.numberOfPayments > 1
    // Noun phrase ending in "will be charged" so it slots into the trial
    // ("... on {date}.") template below.
    const charge = monthly
      ? `my first monthly installment of ${amount} will be charged`
      : `my card will be charged ${amount}`

    const message = trial
      ? `I understand that if I don't cancel my trial within the ${trialLength}-day window, ` +
        `${charge} on ${formatDate(addDays(today || new Date(), trialLength))}.`
      : monthly
        ? "I understand that I'm signing up for an annual commitment paid in monthly installments."
        : "I understand that I'm signing up for an annual commitment."

    return (
      <label className="step3-payment__acknowledgment" data-testid="step3-charge-ack-label">
        <input
          type="checkbox"
          className="step3-payment__checkbox"
          data-testid="step3-charge-ack"
          checked={this.state.chargeAcknowledged}
          onChange={this.handleChargeAck}
        />{' '}
        {message}
      </label>
    )
  }

  private renderTermsAcknowledgment() {
    return (
      <Label
        htmlFor="terms_of_service"
        className="step3-payment__acknowledgment terms-checkbox-label"
      >
        <CheckBox
          name="terms_of_service"
          data-testid="step3-terms"
          defaultChecked={this.props.termsOfService}
          onChange={this.handleTermsAccepted}
        />{' '}
        I have read the above and agree to the{' '}
        <a href="/terms" title="Terms of Service" target="_blank">
          Terms of Service
        </a>
        .
      </Label>
    )
  }
}
